import React from 'react';
import clsx from 'clsx';

import {faTimes, faUsers} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
	Avatar,
	ListItem,
	type ListItemProps,
	ListItemAvatar,
	ListItemText,
	ListItemSecondaryAction,
} from '@mui/material';
import ColoredAvatar from '@netx/core-components/ColoredAvatar';
import {IconButton} from '@netx/core-components/IconButton';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {USER_GROUP_ITEM_TYPE_LABEL} from '../../locales';
import {
	userSelectorListItemClasses,
	getUserSelectorListItemUtilityClass,
	type UserSelectorListItemClasses,
	USER_SELECTOR_LIST_ITEM_CLASS_NAME,
} from './userSelectorListItemClasses';

export type UserSelectorListItemOwnerState = WithClasses<
	UserSelectorListItemClasses,
	{
		selected?: boolean;
	}
>;

const useUtilityClasses = (ownerState: UserSelectorListItemOwnerState) => {
	const {classes, selected} = ownerState;
	const slots = {
		root: ['root', selected && 'selected'],
		actions: ['actions'],
	};
	return composeClasses(slots, getUserSelectorListItemUtilityClass, classes);
};

const UserSelectorListItemRoot = styled(ListItem)(({theme}) => ({
	borderRadius: '28px 0 0 28px',
	outline: 'none !important',
	paddingLeft: 10,
	marginLeft: theme.spacing(2),
	width: `calc(100% - ${theme.spacing(2)}) !important`,
	[`&:hover, &:focus, &.${userSelectorListItemClasses.selected}`]: {
		backgroundColor: theme.palette.action.hover,
	},
}));

const UserSelectorListItemActions = styled('div')(({theme}) => ({
	flex: '0 0 auto',
	paddingLeft: theme.spacing(2),
}));

export type UserSelectorListItemProps = Partial<
	Omit<ListItemProps<'li'>, 'button' | 'classes' | 'id' | 'onChange'>
> &
	WithClasses<
		UserSelectorListItemClasses,
		{
			hideSubtitle?: boolean;
			onRemove?: () => void;
			name: string;
			id: number;
			type: 'user' | 'group';
		}
	>;

export const UserSelectorListItemPrimitive = ({
	classes: _classes,
	className,
	hideSubtitle,
	name,
	id,
	type,
	onRemove,
	selected,
	...props
}: UserSelectorListItemProps) => {
	const ownerState: UserSelectorListItemOwnerState = {
		classes: _classes,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<UserSelectorListItemRoot
			data-id={`${type}-${id}`}
			className={clsx(classes.root, className)}
			id={`user-selector-item-${type}-${id}`}
			{...props}
		>
			<ListItemAvatar aria-hidden="true">
				{id === -1 ? (
					<Avatar>
						<FontAwesomeIcon icon={faUsers} />
					</Avatar>
				) : (
					<ColoredAvatar label={name} />
				)}
			</ListItemAvatar>
			<ListItemText
				primaryTypographyProps={{
					noWrap: true,
				}}
				primary={name}
				secondary={!hideSubtitle ? USER_GROUP_ITEM_TYPE_LABEL[type] : undefined}
				role="presentation"
			/>
			<UserSelectorListItemActions role="presentation">
				{onRemove ? (
					<ListItemSecondaryAction>
						<IconButton
							edge="end"
							label={i18n.t('i18n.remove')}
							icon={faTimes}
							onClick={onRemove}
						/>
					</ListItemSecondaryAction>
				) : null}
			</UserSelectorListItemActions>
		</UserSelectorListItemRoot>
	);
};

export const UserSelectorListItem = UserSelectorListItemPrimitive;

export default UserSelectorListItem;
